import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_ATTEMPTS,
  countOutbox,
  deleteFromOutbox,
  getOutboxItem,
  listOutbox,
  subscribeOutboxChanges,
  updateOutboxItem,
  type OutboxPost,
} from './outbox';
import { sendOutboxItem } from './usePosts';
import { captureException } from '../../lib/sentry';

/**
 * outbox hook (REQ-4.1.15、REQ-4.1.16)。
 *
 * 提供する state:
 *   - `count`: 未送信件数
 *   - `items`: outbox 全件 (UI で個別表示する場合用、現状 AppHome では count のみ使用)
 *   - `isFlushing`: 自動 / 手動 flush 実行中
 *
 * 提供する action:
 *   - `flush(options?: FlushOptions)`: outbox 全件を順次送信
 *     - `skipFailed: true` で status='failed' を除外 (auto flush 用、R-28 第 2 弾)
 *     - 省略 (手動 flush) で failed 含め全 item 再試行
 *     - 戻り値: `{ sent, failed, skipped }`
 *
 * 自動 flush:
 *   - online event でも自動発火 (navigator.onLine → 復帰時、skipFailed:true)
 *   - 初回 mount でも online なら flush 実行 (アプリ起動時の保留 post 送信、skipFailed:true)
 *
 * R-28 後続 (Phase 5):
 *   - 指数 back-off retry (現状は失敗しても次回 flush で再試行のみ)
 *   - expiration (一定回数/期間で破棄)
 *   - status='uploading' (送信中の中間 status)
 *   - retry 単発 button (個別 sendOutboxItem の UI、FlushOptions を index.ts export)
 */

export interface OutboxState {
  count: number;
  /** pending: status 未設定 or 'pending' な item の件数 (R-28 第 1 弾)。 */
  pendingCount: number;
  /** failed: status='failed' (MAX_ATTEMPTS 超過) の件数。 */
  failedCount: number;
  items: OutboxPost[];
  isFlushing: boolean;
}

function classifyCounts(items: OutboxPost[]): { pendingCount: number; failedCount: number } {
  let pendingCount = 0;
  let failedCount = 0;
  for (const item of items) {
    if (item.status === 'failed') failedCount += 1;
    else pendingCount += 1;
  }
  return { pendingCount, failedCount };
}

/**
 * flush 呼び出しオプション (R-28 第 2 弾)。
 *   - `skipFailed: true` で status='failed' な item を skip (自動 retry 用)。
 *   - 手動 flush (UI button) は省略 → 全 item 再試行 (failed 含む)。
 */
export interface FlushOptions {
  skipFailed?: boolean;
}

/**
 * retryOne 戻り値 (R-28 第 3 弾)。
 *   - `ok: true` で送信成功 + 削除済、`ok: false` で失敗 (lastError に内容)。
 *   - `notFound: true` で指定 id の item が見つからなかった (既に削除済等)。
 */
export interface RetryOneResult {
  ok: boolean;
  notFound?: boolean;
  lastError?: string;
}

/**
 * flush と retryOne の failure path 共通 helper (R-28 第 4 弾)。
 *   - attempts インクリメント
 *   - nextAttempts >= MAX_ATTEMPTS で status='failed'、それ以外 'pending'
 *   - lastError は Error.message or String(error)
 *   - item.id が undefined なら no-op (persist 不可)
 * flush / retryOne 両方の catch ブランチから呼ばれ、attempts/status 更新 logic と
 * MAX_ATTEMPTS 閾値判定の重複を解消する。
 */
async function markItemFailedOnce(item: OutboxPost, error: unknown): Promise<string> {
  const lastError = error instanceof Error ? error.message : String(error);
  if (item.id === undefined) return lastError;
  const nextAttempts = item.attempts + 1;
  // MAX_ATTEMPTS 超過で status='failed' に遷移 (UI で別 badge 表示、R-28 第 1 弾)。
  // 'failed' でも手動 flush は引き続き受け付ける (R-28 第 2 弾で auto flush は skip)。
  const nextStatus: OutboxPost['status'] = nextAttempts >= MAX_ATTEMPTS ? 'failed' : 'pending';
  await updateOutboxItem({
    ...item,
    lastError,
    attempts: nextAttempts,
    status: nextStatus,
  });
  // R-69: 自動 retry を尽くして確定失敗 (status='failed') した投稿のみ Sentry に通知 (docs/14 §3-4 Error)。
  // PII (title/comment/location/photo) は送らず、診断に足る非個人情報のみ extra に載せる。
  if (nextStatus === 'failed') {
    captureException(
      error instanceof Error ? error : new Error(lastError),
      {
        source: 'post-outbox',
        attempts: nextAttempts,
        districtId: item.districtId,
        hasPhoto: Boolean(item.photoBlob),
      },
      { kind: 'post.send-failed' },
    );
  }
  // 戻り値: 採用した lastError 文字列 (R-28 第 4 弾 audit 反映、retryOne の二重抽出回避)
  return lastError;
}

export function useOutbox(): {
  state: OutboxState;
  flush: (options?: FlushOptions) => Promise<{ sent: number; failed: number; skipped: number }>;
  retryOne: (id: number) => Promise<RetryOneResult>;
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<OutboxState>({
    count: 0,
    pendingCount: 0,
    failedCount: 0,
    items: [],
    isFlushing: false,
  });

  /**
   * unmount 後の setState を防ぐ guard (R-28 第 4 弾)。
   * flush / retryOne の async chain が完了する前に hook が unmount された場合に
   * setState 呼び出しを skip。React 18 StrictMode 下の double-invoke でも安全。
   * useEffect 内の初回 mount 用 `cancelled` フラグとは別物 (こちらは hook ライフタイム全体)。
   */
  const isMountedRef = useRef(true);

  const refresh = useCallback(async () => {
    const items = await listOutbox();
    if (!isMountedRef.current) return;
    const counts = classifyCounts(items);
    setState((s) => ({ ...s, items, count: items.length, ...counts }));
  }, []);

  const flush = useCallback(async (options?: FlushOptions) => {
    if (isMountedRef.current) {
      setState((s) => ({ ...s, isFlushing: true }));
    }
    let sent = 0;
    let failed = 0;
    let skipped = 0;
    try {
      const allItems = await listOutbox();
      // R-28 第 2 弾: auto-flush (skipFailed=true) では status='failed' を除外し、
      // 失敗確定済 item の自動再試行を抑制 (UI 表示のみに留め、ユーザーの明示的判断を待つ)。
      // 手動 flush は skipFailed 省略 → 全 item 再試行 (failed の手動 retry も提供)。
      const items = options?.skipFailed
        ? allItems.filter((item) => {
            if (item.status === 'failed') {
              skipped += 1;
              return false;
            }
            return true;
          })
        : allItems;
      for (const item of items) {
        try {
          await sendOutboxItem(item);
          if (item.id !== undefined) await deleteFromOutbox(item.id);
          sent += 1;
        } catch (e) {
          failed += 1;
          // R-28 第 4 弾: flush と retryOne で重複していた attempts/status 更新 logic を共通化
          await markItemFailedOnce(item, e);
        }
      }
    } finally {
      const remaining = await listOutbox();
      if (isMountedRef.current) {
        const counts = classifyCounts(remaining);
        setState({
          count: remaining.length,
          items: remaining,
          isFlushing: false,
          ...counts,
        });
      }
    }
    return { sent, failed, skipped };
  }, []);

  /**
   * 1 件だけ再試行 (R-28 第 3 弾)。失敗確定済 (status='failed') item を UI からの
   * 明示的操作で retry するための helper。flush() 全件再試行より軽量。
   * 失敗時は markItemFailedOnce で attempts/status を更新 (flush failure path と共通化)。
   * R-28 第 4 弾: lookup を `getOutboxItem(id)` で O(1) 化、unmount race を isMountedRef で guard。
   */
  const retryOne = useCallback(
    async (id: number): Promise<RetryOneResult> => {
      // R-28 第 4 弾: O(N) listOutbox+find → O(1) primary key 直接 get
      const item = await getOutboxItem(id);
      if (!item) {
        await refresh(); // queue から消えていれば state 同期 (refresh 内で mount guard)
        return { ok: false, notFound: true };
      }
      if (isMountedRef.current) {
        setState((s) => ({ ...s, isFlushing: true }));
      }
      try {
        await sendOutboxItem(item);
        if (item.id !== undefined) await deleteFromOutbox(item.id);
        await refresh();
        return { ok: true };
      } catch (e) {
        // R-28 第 4 弾 audit 反映: markItemFailedOnce が採用した lastError を直接受け取る
        // (旧: 同じ抽出を retryOne 側でも実行していた二重抽出を解消)
        const lastError = await markItemFailedOnce(item, e);
        await refresh();
        return { ok: false, lastError };
      } finally {
        if (isMountedRef.current) {
          setState((s) => ({ ...s, isFlushing: false }));
        }
      }
    },
    [refresh],
  );

  // 初回 mount + online 復帰時に refresh + 自動 flush
  useEffect(() => {
    // mount: isMountedRef を true に戻す (StrictMode の re-mount に対応)
    isMountedRef.current = true;
    let cancelled = false;
    void (async () => {
      const fresh = await listOutbox();
      if (cancelled || !isMountedRef.current) return;
      const counts = classifyCounts(fresh);
      setState({ count: fresh.length, items: fresh, isFlushing: false, ...counts });
      if (fresh.length > 0 && navigator.onLine) {
        // 起動時にも outbox があれば自動 flush (failed item は skip、R-28 第 2 弾)
        void flush({ skipFailed: true });
      }
    })();

    function onOnline() {
      void (async () => {
        const c = await countOutbox();
        // online 復帰時の auto flush も failed item を skip (手動 flush と区別)
        if (c > 0) void flush({ skipFailed: true });
      })();
    }
    window.addEventListener('online', onOnline);
    // 他 tab で outbox 変化したときに refresh (BroadcastChannel 経由、reviewer audit ⚠️ C 反映)。
    // storage event は IndexedDB 変化を検知しないため、outbox.ts の notifyOutboxChanged() で
    // 発火される BroadcastChannel を購読。
    const unsubscribeChanges = subscribeOutboxChanges(() => {
      void refresh();
    });
    return () => {
      cancelled = true;
      isMountedRef.current = false;
      window.removeEventListener('online', onOnline);
      unsubscribeChanges();
    };
  }, [flush, refresh]);

  return { state, flush, retryOne, refresh };
}
